import ClockCircleOutlined from "@ant-design/icons/ClockCircleOutlined";
import { BasicBotData, LanguageNameSpaces } from "@shared/types";
import { typography } from "@shared/common/Themes/typography";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { ACTIVE_TRADE_ROWS_BREAKPOINT } from "@shared/features/bots/constants";
import { ActiveTradePreviewStyles } from "@shared/features/bots/styles";
import { toFixed } from "@shared/utils/numbers";
import Flex from "common/Boxes/Flex";
import { MOBILE_SEARCH } from "common/constants.ts";
import Divider from "common/misc/Divider";
import Text from "common/Typography/Text";
import { CardsLayoutContext } from "features/bots/BotForm/common/CardsLayoutProvider";
import {
  BottomRowWrapper,
  StatisticTitle,
  StatisticWrapper,
} from "features/bots/MyBots/common/styles";
import { useContext } from "react";
import styled from "styled-components";
import useMedia from "utils/hooks/useMedia.ts";

interface ActiveHedgingPreviewProps {
  botData: BasicBotData;
}

const ActiveHedgingPreview = ({ botData }: ActiveHedgingPreviewProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const { estimatedCardSize } = useContext(CardsLayoutContext)!;
  const useActiveTradeRows = estimatedCardSize < ACTIVE_TRADE_ROWS_BREAKPOINT;
  const hideBorder = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const lastGridFilled = botData.last_grid_filled;
  const currentLongHedging = toFixed((botData.current_exposure ?? 0) * 100, 2);
  const currentShortHedging = toFixed(100 - currentLongHedging, 2);

  const getLastGridFilled = () => {
    return (
      <Flex
        $flexDirection={useActiveTradeRows ? "row" : "column"}
        $justifyContent="center"
        $gap={useActiveTradeRows ? 0 : 2}
      >
        #{lastGridFilled?.target_number}/{lastGridFilled?.number_of_targets}
        <Text
          $size={typography.fontSizes.S}
          style={{
            display: "inline",
            marginLeft: useActiveTradeRows ? 10 : 0,
          }}
        >
          <ClockCircleOutlined style={{ marginRight: 4 }} />
          {lastGridFilled?.time_passed} {t("common.ago")}
        </Text>
      </Flex>
    );
  };

  const statisticProps = {
    $useActiveTradeRows: useActiveTradeRows,
    $hideBorder: hideBorder,
  };

  const getActiveHedgingInfo = () => {
    return (
      <BottomRowWrapper $useActiveTradeRows={useActiveTradeRows}>
        {lastGridFilled && (
          <HedgingStatisticWrapper {...statisticProps}>
            <StatisticTitle>{t("labels.last_rebalance")}</StatisticTitle>
            <StyledValue>{getLastGridFilled()}</StyledValue>
          </HedgingStatisticWrapper>
        )}
        {botData.current_exposure !== undefined && (
          <HedgingStatisticWrapper {...statisticProps}>
            <StatisticTitle>{t("labels.current_hedging")}</StatisticTitle>
            <StyledValue>
              <div>
                {t("labels.long")} {currentLongHedging}%
              </div>
              <div>
                {t("labels.short")} {currentShortHedging}%
              </div>
            </StyledValue>
          </HedgingStatisticWrapper>
        )}
      </BottomRowWrapper>
    );
  };

  return (
    <>
      <Divider text={t("labels.active_hedging")} />
      {lastGridFilled ? (
        getActiveHedgingInfo()
      ) : (
        <NoTradeText>{t("labels.no_active_hedging")}</NoTradeText>
      )}
    </>
  );
};

export default ActiveHedgingPreview;

const StyledValue = styled(Text).attrs({
  as: "div",
})`
  font-weight: ${typography.fontWeights.SEMI_BOLD};
  font-size: 14px;
`;
const HedgingStatisticWrapper = styled(StatisticWrapper)<{
  $useActiveTradeRows?: boolean;
  $hideBorder?: boolean;
}>`
  justify-content: flex-start;
  ${(p) =>
    p.$useActiveTradeRows &&
    `
    justify-content: space-between;
  `}
`;
const NoTradeText = styled(Text)`
  ${ActiveTradePreviewStyles.noTradeText};
  height: 100%;
`;
